'use client';

import React, { useEffect, useState, useRef } from 'react';
import { LivePanchangHeroCard } from './LivePanchangHeroCard';

interface AstroWheelProps {
  locale: string;
  labels: {
    sampleChartBadge: string;
    sampleChartDasha: string;
    sampleChartSav: string;
    sampleChartNote: string;
  };
}

export function AstroWheelVisual({ locale, labels }: AstroWheelProps) {
  const [reducedMotion, setReducedMotion] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReducedMotion(mediaQuery.matches);
      const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
      }
    }
  }, []);

  const signs = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓'];
  const planets = [
    { glyph: '☉', lon: 207, col: '#ffd9a0' },
    { glyph: '☽', lon: 48, col: '#eef3ff' },
    { glyph: '♂', lon: 112, col: '#ffb4a6' },
    { glyph: '☿', lon: 221, col: '#c9f0ff' },
    { glyph: '♃', lon: 95, col: '#ffe6b0' },
    { glyph: '♀', lon: 189, col: '#ffd0ef' },
    { glyph: '♄', lon: 338, col: '#d6ccff' },
    { glyph: '☊', lon: 355, col: '#b9c6ff' },
    { glyph: '☋', lon: 175, col: '#b9c6ff' },
  ];

  const C = 300;
  const asc = 250;
  const getPoint = (r: number, lon: number): [number, number] => {
    const theta = ((180 + lon - asc) * Math.PI) / 180;
    return [
      parseFloat((C + r * Math.cos(theta)).toFixed(1)),
      parseFloat((C - r * Math.sin(theta)).toFixed(1)),
    ];
  };

  const cusps = [0, 27, 58, 92, 124, 153, 180, 207, 238, 272, 304, 333];

  return (
    <div
      ref={containerRef}
      className="relative w-full max-w-[480px] sm:max-w-[540px] aspect-square mx-auto flex items-center justify-center select-none"
      data-testid="astro-wheel-visual"
    >
      {/* Background radial glow */}
      <div
        className={`absolute inset-[10%] rounded-full bg-[radial-gradient(circle,rgba(120,110,255,0.35),rgba(60,90,255,0.12)_55%,transparent_72%)] blur-2xl pointer-events-none ${
          reducedMotion ? '' : 'animate-[breathe_6s_ease-in-out_infinite]'
        }`}
      />

      {/* SVG Chart Orrery */}
      <div
        className={`absolute inset-0 w-full h-full pointer-events-none ${
          reducedMotion ? '' : 'animate-[orbitm_300s_linear_infinite]'
        }`}
      >
        <svg
          viewBox="0 0 600 600"
          className="w-full h-full overflow-visible font-sans"
          aria-hidden="true"
        >
          {/* Main concentric rings */}
          <circle cx="300" cy="300" r="296" fill="none" stroke="rgba(214,224,255,0.5)" strokeWidth="1.2" />
          <circle cx="300" cy="300" r="280" fill="none" stroke="rgba(214,224,255,0.35)" strokeWidth="1" />
          <circle cx="300" cy="300" r="272" fill="none" stroke="rgba(214,224,255,0.55)" strokeWidth="1" />
          <circle cx="300" cy="300" r="232" fill="none" stroke="rgba(214,224,255,0.55)" strokeWidth="1" />
          <circle cx="300" cy="300" r="226" fill="none" stroke="rgba(214,224,255,0.25)" strokeWidth="1" />
          <circle cx="300" cy="300" r="150" fill="none" stroke="rgba(214,224,255,0.35)" strokeWidth="1" strokeDasharray="2 4" />
          <circle cx="300" cy="300" r="112" fill="none" stroke="rgba(214,224,255,0.6)" strokeWidth="1" />
          <circle cx="300" cy="300" r="106" fill="none" stroke="rgba(214,224,255,0.2)" strokeWidth="1" />

          {/* 30-degree zodiac division lines */}
          {signs.map((_, i) => {
            const [x1, y1] = getPoint(232, i * 30);
            const [x2, y2] = getPoint(272, i * 30);
            return (
              <line
                key={`zdiv-${i}`}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="rgba(214,224,255,0.55)"
                strokeWidth="1"
              />
            );
          })}

          {/* Zodiac glyphs */}
          {signs.map((glyph, i) => {
            const [x, y] = getPoint(252, i * 30 + 15);
            return (
              <text
                key={`zglyph-${i}`}
                x={x}
                y={y}
                fontSize="22"
                fill="#e3e8ff"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {glyph}
              </text>
            );
          })}

          {/* House cusps */}
          {cusps.map((c, i) => {
            const lon = asc + c;
            const isAngle = i % 3 === 0;
            const [x1, y1] = getPoint(112, lon);
            const [x2, y2] = getPoint(226, lon);
            const nx = cusps[(i + 1) % 12] || 360;
            const [tx, ty] = getPoint(130, asc + (c + nx) / 2);
            return (
              <g key={`cusp-${i}`}>
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="rgba(214,224,255,0.5)"
                  strokeWidth={isAngle ? 1.4 : 0.8}
                />
                <text
                  x={tx}
                  y={ty}
                  fontSize="11"
                  fill="rgba(200,212,255,0.6)"
                  fontFamily="Lexend"
                  textAnchor="middle"
                  dominantBaseline="central"
                >
                  {i + 1}
                </text>
              </g>
            );
          })}

          {/* Planetary positions and glyphs */}
          {planets.map((p, idx) => {
            const [x, y] = getPoint(106, p.lon);
            const [tx, ty] = getPoint(194, p.lon);
            return (
              <g key={`planet-${idx}`}>
                <circle cx={x} cy={y} r="2.6" fill={p.col} />
                <text
                  x={tx}
                  y={ty}
                  fontSize="21"
                  fill={p.col}
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ filter: `drop-shadow(0 0 6px ${p.col})` }}
                >
                  {p.glyph}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Floating Card 1: Top Left Live Panchang */}
      <div className="absolute -top-3 -left-2 sm:-left-8 z-10">
        <LivePanchangHeroCard locale={locale} />
      </div>

      {/* Floating Card 2: Bottom Right Sample Chart (Replaces Pattern Match) */}
      <div className="absolute -bottom-4 -right-2 sm:-right-6 z-10 max-w-[230px]">
        <div
          className="p-3.5 sm:p-4 rounded-2xl flex flex-col gap-1.5 bg-gradient-to-br from-white/16 to-white/[0.03] border border-white/28 shadow-[inset_0_1px_0_rgba(255,255,255,0.45),0_20px_50px_-10px_rgba(0,8,40,0.7)] backdrop-blur-xl animate-[float_8s_ease-in-out_-3s_infinite]"
          data-testid="sample-chart-card"
        >
          <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-wider text-[#8dffc9]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#8dffc9]" />
            <span>{labels.sampleChartBadge}</span>
          </div>
          <div className="text-xs font-semibold text-white leading-snug">
            {labels.sampleChartDasha}
          </div>
          <div className="text-[11px] text-[rgba(220,236,255,0.8)] leading-tight">
            {labels.sampleChartSav}
          </div>
          <div className="text-[9px] text-[rgba(220,236,255,0.5)] pt-1 border-t border-white/10">
            {labels.sampleChartNote}
          </div>
        </div>
      </div>
    </div>
  );
}
